import React, { useCallback, useEffect, useState } from 'react';
import { Card, Typography, Button, Space, Spin, message, Upload, Tag, Image, List, Empty, Pagination } from 'antd';
import { ArrowLeftOutlined, CameraOutlined, ExperimentOutlined, HistoryOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, TongueCheckin, tongueImageUrl } from '../utils/api';

const { Title, Text, Paragraph } = Typography;

const PAGE_SIZE = 5;

// 渲染单条打卡结果卡片（最新结果与历史详情共用）
const CheckinResultCard: React.FC<{ checkin: TongueCheckin; title: string }> = ({ checkin, title }) => (
  <Card
    style={{
      borderRadius: '16px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      border: 'none',
      marginBottom: '24px',
    }}
    title={title}
  >
    <div style={{ textAlign: 'center', marginBottom: '16px' }}>
      <Image
        src={tongueImageUrl(checkin.image_url)}
        alt="舌象照片"
        style={{ maxWidth: '240px', borderRadius: '12px' }}
      />
      <div style={{ marginTop: '8px' }}>
        <Text type="secondary">{checkin.created_at}</Text>
      </div>
    </div>

    <div style={{ marginBottom: '16px', textAlign: 'center' }}>
      {checkin.labels.length > 0 ? (
        checkin.labels.map((label) => (
          <Tag color="volcano" key={label} style={{ fontSize: '14px', padding: '4px 12px', marginBottom: '8px' }}>
            {label}
          </Tag>
        ))
      ) : (
        <Text type="secondary">未识别到明显舌象特征</Text>
      )}
    </div>

    {checkin.label_infos.map((info) => (
      <Card
        type="inner"
        key={info.label}
        title={<span style={{ color: '#cf1322' }}>{info.label}</span>}
        style={{ marginBottom: '12px' }}
      >
        <Paragraph style={{ marginBottom: '8px' }}>
          <Text strong>意义解读：</Text>
          {info.description}
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>调养建议：</Text>
          {info.suggestion}
        </Paragraph>
      </Card>
    ))}

    <div style={{ textAlign: 'center', marginTop: '16px' }}>
      <Text type="secondary" style={{ fontSize: '12px' }}>
        以上结果为 AI 辅助识别，仅供健康参考，不构成医学诊断
      </Text>
    </div>
  </Card>
);

const TonguePage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [recognizing, setRecognizing] = useState(false);
  const [latestResult, setLatestResult] = useState<TongueCheckin | null>(null);
  const [selectedCheckin, setSelectedCheckin] = useState<TongueCheckin | null>(null);

  const [history, setHistory] = useState<TongueCheckin[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 检查用户是否已登录
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
  }, [authState.isLoading, authState.isAuthenticated, navigate]);

  // 加载打卡历史
  const loadHistory = useCallback(async (page: number) => {
    if (!authState.token) {
      return;
    }
    try {
      setHistoryLoading(true);
      const response = await api.getTongueList({ page, page_size: PAGE_SIZE }, authState.token);
      if (response.status === 200 && response.data) {
        setHistory(response.data.checkins || []);
        setHistoryTotal(response.data.total || 0);
        setHistoryPage(response.data.page || page);
      } else {
        message.error(response.msg || '获取打卡历史失败');
      }
    } catch (error) {
      console.error('获取打卡历史失败:', error);
      message.error('获取打卡历史失败');
    } finally {
      setHistoryLoading(false);
    }
  }, [authState.token]);

  useEffect(() => {
    if (authState.isAuthenticated && authState.token) {
      loadHistory(1);
    }
  }, [authState.isAuthenticated, authState.token, loadHistory]);

  // 选择图片（不上传，本地预览）
  const handleSelectFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('请选择图片文件');
      return false;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    return false;
  };

  // 开始识别
  const handleRecognize = async () => {
    if (!selectedFile) {
      message.warning('请先选择或拍摄舌头照片');
      return;
    }
    if (!authState.token) {
      message.error('请先登录');
      return;
    }
    try {
      setRecognizing(true);
      const response = await api.tongueCheckin(selectedFile, authState.token);
      if (response.status === 200 && response.data) {
        message.success('识别完成！');
        setLatestResult(response.data.checkin);
        setSelectedCheckin(null);
        setSelectedFile(null);
        setPreviewUrl('');
        loadHistory(1);
      } else {
        message.error(response.msg || '识别失败，请重试');
      }
    } catch (error) {
      console.error('舌诊识别失败:', error);
      message.error('识别失败，请稍后重试');
    } finally {
      setRecognizing(false);
    }
  };

  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 当前展示的打卡记录：优先显示选中的历史记录，其次最新识别结果
  const displayCheckin = selectedCheckin || latestResult;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px 16px',
    }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {/* 顶部导航 */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
            style={{ color: 'white', fontSize: '16px' }}
          >
            返回
          </Button>
          <Title level={3} style={{ color: 'white', margin: '0 auto', paddingRight: '64px' }}>
            舌诊打卡
          </Title>
        </div>

        {/* 上传区 */}
        <Card
          style={{
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: 'none',
            marginBottom: '24px',
            textAlign: 'center',
          }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text>拍摄或选择一张清晰的舌头照片（请在自然光下拍摄，舌面完整露出）</Text>
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={handleSelectFile}
            >
              <Button icon={<CameraOutlined />} size="large">
                选择 / 拍摄照片
              </Button>
            </Upload>
            {previewUrl && (
              <div>
                <img
                  src={previewUrl}
                  alt="预览"
                  style={{ maxWidth: '200px', borderRadius: '12px' }}
                />
              </div>
            )}
            {selectedFile && (
              <Button
                type="primary"
                size="large"
                icon={<ExperimentOutlined />}
                onClick={handleRecognize}
                loading={recognizing}
                style={{ background: '#764ba2', borderColor: '#764ba2' }}
              >
                {recognizing ? '识别中，可能需要数十秒…' : '开始识别'}
              </Button>
            )}
          </Space>
        </Card>

        {/* 识别中提示 */}
        {recognizing && (
          <Card style={{ borderRadius: '16px', marginBottom: '24px', textAlign: 'center', border: 'none' }}>
            <Spin size="large" />
            <div style={{ marginTop: '12px' }}>
              <Text type="secondary">AI 正在识别舌象，请稍候…</Text>
            </div>
          </Card>
        )}

        {/* 结果展示区 */}
        {displayCheckin && !recognizing && (
          <CheckinResultCard
            checkin={displayCheckin}
            title={selectedCheckin ? '历史打卡详情' : '本次识别结果'}
          />
        )}

        {/* 历史记录 */}
        <Card
          style={{
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: 'none',
          }}
          title={
            <span>
              <HistoryOutlined style={{ marginRight: '8px' }} />
              打卡历史
            </span>
          }
        >
          {history.length === 0 && !historyLoading ? (
            <Empty description="还没有打卡记录，上传第一张舌头照片吧" />
          ) : (
            <>
              <List
                loading={historyLoading}
                dataSource={history}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedCheckin(item);
                      setLatestResult(null);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    actions={[
                      <Button
                        type="link"
                        key="detail"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCheckin(item);
                          setLatestResult(null);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        详情
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <img
                          src={tongueImageUrl(item.image_url)}
                          alt="舌象"
                          style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      }
                      title={item.created_at}
                      description={
                        item.labels.length > 0
                          ? item.labels.map((label) => (
                              <Tag key={label} color="volcano" style={{ marginBottom: '4px' }}>
                                {label}
                              </Tag>
                            ))
                          : '未识别到明显舌象特征'
                      }
                    />
                  </List.Item>
                )}
              />
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Pagination
                  current={historyPage}
                  total={historyTotal}
                  pageSize={PAGE_SIZE}
                  onChange={(page) => loadHistory(page)}
                  showSizeChanger={false}
                  size="small"
                />
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default TonguePage;
